import { Component, computed, effect, inject, input, type OnInit, output } from '@angular/core';
import { signalHelpers, utils } from '@core';
import { MultipleSelectComponent } from '@ui';

import { FiltersStore } from '../../../stores';
import { type FiltersState } from '../../../stores/common-filters.store';
import { LocationFiltersComponent } from '../location-filters';

@Component({
  host: {
    class: 'flex items-start flex-wrap gap-2',
  },
  imports: [
    MultipleSelectComponent,
    LocationFiltersComponent,
  ],
  selector: 'avs-li-filters-bar',
  templateUrl: './filters-bar.component.html',
})
export class FiltersBarComponent implements OnInit {
  filters = input<Partial<FiltersState>>();
  showUI = input(true);
  filtersChanged = output<Record<string, string | string[]>>();
  id = input<string>('');

  inputConfig = input<{
    building?: boolean;
    floor?: boolean;
  }>({}, { alias: 'config' });

  protected config = computed(() => ({
    building: true,
    floor: true,
    ...this.inputConfig(),
  }));

  protected filtersStore = inject(FiltersStore);
  protected hasSelectedFilters = computed(() =>
    utils.hasValue({
      ...this.config().building && { building: this.filtersStore.building() },
      ...this.config().floor && { floor: this.filtersStore.floor() },
      deviceMaker: this.filtersStore.deviceMaker(),
      deviceType: this.filtersStore.deviceType(),
      room: this.filtersStore.room(),
    }),
  );

  constructor() {
    signalHelpers.untrackedEffect([this.filters], filters => {
      const { building, floor, room, deviceType } = filters ?? {};

      this.filtersStore.patchFilters({
        ...building && { building },
        ...floor && { floor },
        ...room && { room },
        ...(deviceType && deviceType.length > 0 ? { deviceType } : {}),
      });
    }, { skipFirst: true });

    effect(() => this.filtersChanged.emit(this.filtersStore.appliedFilters()));
  }

  async ngOnInit() {
    const { building, floor, room, deviceType } = this.filters() ?? {};

    this.filtersStore.setTableId(this.id());
    this.filtersStore.initDefaultFilters({
      ...building && { building },
      ...floor && { floor },
      ...room && { room },
      ...(deviceType && deviceType.length > 0 ? { deviceType } : {}),
    });

    await this.filtersStore.fetchOptions(room ? { space: room } : undefined);
  }

  protected clearFilters() {
    this.filtersStore.patchFilters({
      ...this.config().building && { building: '' },
      ...this.config().floor && { floor: '' },
      deviceMaker: [],
      deviceType: [],
      room: '',
    });
  }
}
